// Date filtering utilities for analytics dashboard

export type DateRange = 'today' | 'this-week' | 'month-to-date' | 'last-month' | 'last-90-days' | 'last-year' | 'all-time';

export interface DateRangeOption {
  value: DateRange;
  label: string;
}

export const dateRangeOptions: DateRangeOption[] = [
  { value: 'today', label: 'Today' },
  { value: 'this-week', label: 'This Week' },
  { value: 'month-to-date', label: 'Month to Date' },
  { value: 'last-month', label: 'Last Month' },
  { value: 'last-90-days', label: 'Last 90 Days' },
  { value: 'last-year', label: 'Last Year' },
  { value: 'all-time', label: 'All Time' },
];

// Get start and end timestamps for a date range
export function getDateRangeBounds(range: DateRange): { start: number; end: number } {
  const now = new Date();
  const end = now.getTime();
  let start: number;

  switch (range) {
    case 'today': {
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      start = todayStart.getTime();
      break;
    }
    case 'this-week': {
      const dayOfWeek = now.getDay();
      const weekStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() - dayOfWeek);
      start = weekStart.getTime();
      break;
    }
    case 'month-to-date': {
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      start = monthStart.getTime();
      break;
    }
    case 'last-month': {
      const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
      start = lastMonthStart.getTime();
      return { start, end: lastMonthEnd.getTime() };
    }
    case 'last-90-days': {
      const ninetyDaysAgo = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
      start = ninetyDaysAgo.getTime();
      break;
    }
    case 'last-year': {
      const oneYearAgo = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
      start = oneYearAgo.getTime();
      break;
    }
    case 'all-time':
    default:
      start = 0;
      break;
  }

  return { start, end };
}

// Format date for display
export function formatDate(timestamp: number): string {
  const date = new Date(timestamp);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// Get date key for grouping (YYYY-MM-DD)
export function getDateKey(timestamp: number): string {
  const date = new Date(timestamp);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}